import { orgAPI } from "@/api/api";
import { useQuery } from "@tanstack/react-query";

interface UseNotionPageFetchProps {
  connectionId: string | undefined;
  pageId: string;
  enabled?: boolean;
}

// Parse the Notion page content from the MCP response
function parseNotionPageResponse(rawResponse: any, fallbackPageId: string) {
  console.log("Raw Notion response:", rawResponse);

  // The response is directly the data object (not wrapped in an array)
  const data = rawResponse;

  if (!data || typeof data !== "object") {
    throw new Error("Invalid response format: expected object");
  }

  // Extract markdown content from the XML-wrapped text
  let body = "";
  if (data.text) {
    // The text field contains XML-like format: <page><content>markdown here</content></page>
    // Extract content between <content> tags
    const contentMatch = data.text.match(/<content>([\s\S]*?)<\/content>/);
    if (contentMatch) {
      body = contentMatch[1].trim();
    } else {
      // Fallback: use the whole text if no content tags found
      body = data.text;
    }
  }

  return {
    id: data.id || fallbackPageId,
    title: data.title || "Untitled Page",
    body,
    web_link: data.url,
    created_at: data.created_at,
    updated_at: data.updated_at,
    metadata: data.metadata,
  };
}

export function useNotionPageFetch({
  connectionId,
  pageId,
  enabled = true,
}: UseNotionPageFetchProps) {
  return useQuery({
    queryKey: ["notion-page", connectionId, pageId],
    queryFn: async () => {
      if (!connectionId) {
        throw new Error("No Notion connection available");
      }

      const response = await orgAPI.toolbox.execute({
        integration_connection_id: connectionId,
        action_name: "notion-mcp-fetch",
        action_input: { id: pageId },
      });

      console.log("Notion page fetch response:", response);

      if (!response.data) {
        console.error("No data in response:", response);
        throw new Error("Failed to fetch Notion page: No data returned");
      }

      return parseNotionPageResponse(response.data, pageId);
    },
    enabled: !!connectionId && !!pageId && enabled,
    staleTime: 30 * 1000,
    retry: 1,
  });
}
